import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api.js';
import { getProgramImage } from '../lib/program-images.js';

interface OnboardingWizardProps {
  orgId: string;
  orgName: string;
  onComplete: () => void;
}

const PRESET_PROGRAMS = [
  'Kids Martial Arts',
  'Adult Martial Arts',
  'Brazilian Jiu-Jitsu',
  'Muay Thai',
  'Karate',
  'Taekwondo',
  'Boxing',
  'MMA',
  'Kickboxing',
  'Fitness / Boot Camp',
  'Yoga',
  'Self-Defense',
];

function getPreSelectedPrograms(orgName: string): string[] {
  const name = orgName.toLowerCase();
  const selected: string[] = [];

  if (name.includes('bjj') || name.includes('jiu jitsu') || name.includes('jiu-jitsu')) {
    selected.push('Brazilian Jiu-Jitsu');
  }
  if (name.includes('karate')) {
    selected.push('Karate');
  }
  if (name.includes('tkd') || name.includes('taekwondo')) {
    selected.push('Taekwondo');
  }
  if (name.includes('fitness') || name.includes('fit') || name.includes('crossfit') || name.includes('boot')) {
    selected.push('Fitness / Boot Camp');
  }
  if (name.includes('boxing') && !name.includes('kick')) {
    selected.push('Boxing');
  }
  if (name.includes('mma')) {
    selected.push('MMA');
  }
  if (name.includes('muay thai') || name.includes('kickbox')) {
    selected.push('Muay Thai');
    if (!selected.includes('Kickboxing')) selected.push('Kickboxing');
  }
  if (name.includes('yoga')) {
    selected.push('Yoga');
  }

  if (selected.length === 0) {
    return ['Kids Martial Arts', 'Adult Martial Arts'];
  }
  return selected;
}

export function OnboardingWizard({ orgId, orgName, onComplete }: OnboardingWizardProps) {
  const [step, setStep] = useState(1);
  const [hasExistingSite, setHasExistingSite] = useState<boolean | null>(null);
  const [existingUrl, setExistingUrl] = useState('');
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>(() =>
    getPreSelectedPrograms(orgName)
  );
  const [customProgram, setCustomProgram] = useState('');
  const [saving, setSaving] = useState(false);

  // Notification animation state
  const [showNotif, setShowNotif] = useState(false);

  useEffect(() => {
    if (step === 3) {
      const timer = setTimeout(() => setShowNotif(true), 400);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const toggleProgram = (program: string) => {
    setSelectedPrograms((prev) =>
      prev.includes(program) ? prev.filter((p) => p !== program) : [...prev, program]
    );
  };

  const addCustomProgram = () => {
    const trimmed = customProgram.trim();
    if (trimmed && !selectedPrograms.includes(trimmed)) {
      setSelectedPrograms((prev) => [...prev, trimmed]);
      setCustomProgram('');
    }
  };

  const handleSkip = async () => {
    try {
      await apiFetch(`/orgs/${orgId}/config`, {
        method: 'PUT',
        body: JSON.stringify({
          onboarding_step: 'skipped',
          onboarding_skipped_at: new Date().toISOString(),
        }),
      });
    } catch {
      // Non-blocking — let them through regardless
    }
    onComplete();
  };

  const handleNextStep1 = () => {
    // Just move to step 2 — we'll batch-save everything on "Launch My Site"
    setStep(2);
  };

  const handleLaunch = async () => {
    setSaving(true);
    try {
      // 1. Save org config (website info + onboarding status)
      await apiFetch(`/orgs/${orgId}/config`, {
        method: 'PUT',
        body: JSON.stringify({
          has_existing_site: hasExistingSite ?? false,
          existing_website_url: hasExistingSite ? existingUrl : null,
          onboarding_step: 'completed',
          onboarding_completed_at: new Date().toISOString(),
        }),
      });

      // 2. Bulk create programs
      if (selectedPrograms.length > 0) {
        await apiFetch(`/orgs/${orgId}/programs/bulk`, {
          method: 'POST',
          body: JSON.stringify({
            programs: selectedPrograms.map((name) => ({ name, image_url: getProgramImage(name) })),
          }),
        });
      }

      // Move to confirmation screen
      setStep(3);
    } catch (err) {
      console.error('Onboarding save failed:', err);
      // Still show confirmation — data can be added later
      setStep(3);
    } finally {
      setSaving(false);
    }
  };

  const handleSkipPrograms = async () => {
    setSaving(true);
    try {
      await apiFetch(`/orgs/${orgId}/config`, {
        method: 'PUT',
        body: JSON.stringify({
          has_existing_site: hasExistingSite ?? false,
          existing_website_url: hasExistingSite ? existingUrl : null,
          onboarding_step: 'completed',
          onboarding_completed_at: new Date().toISOString(),
        }),
      });
    } catch {
      // Non-blocking
    }
    setSaving(false);
    setStep(3);
  };

  const firstProgram = selectedPrograms[0] ?? 'Kids Martial Arts';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 sm:p-8">
          {step === 1 && (
            <>
              {/* Step indicator */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="w-2 h-2 rounded-full bg-primary-600" />
                <div className="w-2 h-2 rounded-full bg-gray-200" />
                <span className="text-xs text-gray-400 ml-2">Step 1 of 2</span>
              </div>

              <h2 className="text-xl font-bold text-gray-900 text-center">
                Let's get your studio online
              </h2>
              <p className="text-sm text-gray-500 text-center mt-2 mb-8">
                This takes about 60 seconds. We'll build your website and start
                sending you lead notifications.
              </p>

              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Do you have a website already?
              </h3>

              <div className="space-y-3">
                {/* Yes option */}
                <label
                  className={`block rounded-lg border-2 p-4 cursor-pointer transition-colors ${
                    hasExistingSite === true
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="has_site"
                      checked={hasExistingSite === true}
                      onChange={() => setHasExistingSite(true)}
                      className="mt-0.5 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">
                        Yes, here's my current site
                      </span>
                      {hasExistingSite === true && (
                        <div className="mt-3">
                          <input
                            type="url"
                            value={existingUrl}
                            onChange={(e) => setExistingUrl(e.target.value)}
                            placeholder="e.g., www.downtownmartialarts.com"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                          <p className="text-xs text-gray-400 mt-1.5">
                            We'll bring over your best content automatically.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </label>

                {/* No option */}
                <label
                  className={`block rounded-lg border-2 p-4 cursor-pointer transition-colors ${
                    hasExistingSite === false
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="has_site"
                      checked={hasExistingSite === false}
                      onChange={() => setHasExistingSite(false)}
                      className="mt-0.5 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        Nope, I'm starting fresh
                      </span>
                      <p className="text-xs text-gray-400 mt-1">
                        We'll create everything from scratch for you.
                      </p>
                    </div>
                  </div>
                </label>
              </div>

              <button
                onClick={handleNextStep1}
                className="w-full mt-6 bg-primary-600 text-white py-2.5 px-4 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Next
              </button>
              <button
                onClick={handleSkip}
                className="w-full mt-2 text-sm text-gray-400 hover:text-gray-600 transition-colors py-1"
              >
                Skip for now
              </button>
            </>
          )}

          {step === 2 && (
            <>
              {/* Step indicator */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="w-2 h-2 rounded-full bg-primary-600" />
                <div className="w-2 h-2 rounded-full bg-primary-600" />
                <span className="text-xs text-gray-400 ml-2">Step 2 of 2</span>
              </div>

              <h2 className="text-xl font-bold text-gray-900 text-center">
                What do you teach?
              </h2>
              <p className="text-sm text-gray-500 text-center mt-2 mb-6">
                Select your programs. Each one becomes a section on your website
                with its own lead capture form.
              </p>

              {/* Program grid */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {PRESET_PROGRAMS.map((program) => (
                  <label
                    key={program}
                    className={`flex items-center gap-2.5 rounded-lg border p-3 cursor-pointer transition-colors text-sm ${
                      selectedPrograms.includes(program)
                        ? 'border-primary-600 bg-primary-50 text-primary-900'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPrograms.includes(program)}
                      onChange={() => toggleProgram(program)}
                      className="rounded text-primary-600 focus:ring-primary-500"
                    />
                    <span className="font-medium leading-tight">{program}</span>
                  </label>
                ))}
              </div>

              {/* Custom programs */}
              {selectedPrograms
                .filter((p) => !PRESET_PROGRAMS.includes(p))
                .map((p) => (
                  <div
                    key={p}
                    className="flex items-center gap-2 mb-2 rounded-lg border border-primary-600 bg-primary-50 p-3 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked
                      onChange={() => toggleProgram(p)}
                      className="rounded text-primary-600 focus:ring-primary-500"
                    />
                    <span className="font-medium text-primary-900">{p}</span>
                  </div>
                ))}

              {/* Add custom */}
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-500 mb-1.5">
                  Don't see yours? Add it here
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customProgram}
                    onChange={(e) => setCustomProgram(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomProgram();
                      }
                    }}
                    placeholder="e.g., Weapons Training, Tai Chi"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    onClick={addCustomProgram}
                    disabled={!customProgram.trim()}
                    className="px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-40"
                  >
                    + Add
                  </button>
                </div>
              </div>

              <p className="text-xs text-gray-400 mt-3 mb-5">
                You can always add or change programs later.
              </p>

              <button
                onClick={handleLaunch}
                disabled={saving}
                className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg text-sm font-semibold hover:bg-primary-700 transition-colors disabled:opacity-60"
              >
                {saving ? 'Launching...' : 'Launch My Site'}
              </button>
              <button
                onClick={handleSkipPrograms}
                disabled={saving}
                className="w-full mt-2 text-sm text-gray-400 hover:text-gray-600 transition-colors py-1"
              >
                I'll add these later
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <div className="text-center">
                {/* Animated checkmark */}
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-7 h-7 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                <h2 className="text-xl font-bold text-gray-900">
                  We're building your website!
                </h2>
                <p className="text-sm text-gray-500 mt-2 mb-6">
                  Our AI is creating your site right now. You'll get a
                  notification when it's ready to preview.
                </p>
              </div>

              {/* Mock lead notification */}
              <div
                className={`bg-white border-2 border-primary-100 rounded-xl p-4 mb-4 shadow-sm transition-all duration-500 ${
                  showNotif
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-primary-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-primary-600 uppercase tracking-wide">
                      New Lead
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                      Sarah M. is interested in{' '}
                      <span className="text-primary-700">{firstProgram}</span> at{' '}
                      <span className="text-primary-700">{orgName}</span>
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      <span>sarah.m@email.com</span>
                      <span className="w-px h-3 bg-gray-200" />
                      <span>(555) 123-4567</span>
                    </div>
                  </div>
                </div>
              </div>

              <p
                className={`text-xs text-center text-gray-500 mb-6 transition-all duration-500 delay-200 ${
                  showNotif ? 'opacity-100' : 'opacity-0'
                }`}
              >
                This is what you'll see every time someone fills out a form on
                your new website.
              </p>

              {/* While you wait */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  While you wait
                </p>
                <div className="space-y-2">
                  <a
                    href="/app/settings?tab=location"
                    className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 font-medium"
                  >
                    <span>Add your studio address</span>
                    <span className="text-gray-300">&rarr;</span>
                  </a>
                  <a
                    href="/app/settings?tab=organization"
                    className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 font-medium"
                  >
                    <span>Upload your logo</span>
                    <span className="text-gray-300">&rarr;</span>
                  </a>
                  <a
                    href="/app/team"
                    className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 font-medium"
                  >
                    <span>Invite your team</span>
                    <span className="text-gray-300">&rarr;</span>
                  </a>
                </div>
              </div>

              <button
                onClick={onComplete}
                className="w-full bg-primary-600 text-white py-2.5 px-4 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Go to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
